import React from 'react';
import styled from 'styled-components';
import { Card } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import colors from 'Styles/Colors';

const TotalRevenueCard = ({ data }) => {
  // Calculate totals
  const stallsRevenue = data?.stallsSummary?.reduce((sum, item) => sum + (item.revenue || 0), 0) || 0;
  const rvsRevenue = data?.rvsSummary?.reduce((sum, item) => sum + (item.revenue || 0), 0) || 0;
  const addOnsRevenue = data?.addOnsSummary?.reduce((sum, item) => sum + (item.revenue || 0), 0) || 0;
  const miscChargesRevenue = data?.miscChargesSummary?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
  const cancellationRevenue = data?.cancellationRevenue || 0;

  // Calculate total refund amount (refunds are already negative amounts)
  const totalRefundAmount =
    data?.refundsSummary?.reduce((sum, refund) => {
      const refundAmount =
        refund.payments?.reduce((paymentSum, payment) => {
          return paymentSum + (payment.amount || 0);
        }, 0) || 0;
      return sum + refundAmount;
    }, 0) || 0;

  const totalRevenue = stallsRevenue + rvsRevenue + addOnsRevenue + miscChargesRevenue + cancellationRevenue + totalRefundAmount;

  // Count total orders (including canceled)
  const totalOrders = data?.ordersList?.length || 0;

  // Chart data
  const chartData = {
    labels: ['Stalls', 'RVs', 'Add-ons', 'Misc Charges', 'Cancellation Revenue'],
    datasets: [
      {
        data: [stallsRevenue, rvsRevenue, addOnsRevenue, miscChargesRevenue, cancellationRevenue],
        backgroundColor: [
          '#2875c3', // Blue for stalls
          '#10AC84', // Green for RVs
          '#ff9f43', // Orange for add-ons
          '#8395a7', // Gray for misc charges
          '#576574' // Dark gray for cancellation
        ],
        borderWidth: 0,
        hoverOffset: 4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            return `${context.label}: $${value.toFixed(2)}`;
          }
        }
      }
    },
    elements: {
      arc: {
        borderWidth: 0
      }
    }
  };

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <RevenueCard>
      <Card title="Total Revenue" className="dashboard-card">
        <div className="chart-container">
          <div className="chart-wrapper">
            <Doughnut data={chartData} options={chartOptions} />
            <div className="center-value">
              <div className="order-count">{totalOrders}</div>
              <div className="label">Orders</div>
            </div>
          </div>
          <div className="total-amount">
            <div className="amount">{formatCurrency(totalRevenue)}</div>
          </div>
        </div>

        <div className="breakdown">
          <div className="breakdown-item">
            <div className="color-indicator stalls"></div>
            <span className="label">Stalls</span>
            <span className="amount">{formatCurrency(stallsRevenue)}</span>
          </div>
          <div className="breakdown-item">
            <div className="color-indicator rvs"></div>
            <span className="label">RVs</span>
            <span className="amount">{formatCurrency(rvsRevenue)}</span>
          </div>
          <div className="breakdown-item">
            <div className="color-indicator addons"></div>
            <span className="label">Add-ons</span>
            <span className="amount">{formatCurrency(addOnsRevenue)}</span>
          </div>
          <div className="breakdown-item">
            <div className="color-indicator misc"></div>
            <span className="label">Misc Charges</span>
            <span className="amount">{formatCurrency(miscChargesRevenue)}</span>
          </div>
          <div className="breakdown-item">
            <div className="color-indicator cancellation"></div>
            <span className="label">Cancellation Revenue</span>
            <span className="amount">{formatCurrency(cancellationRevenue)}</span>
          </div>
          {totalRefundAmount !== 0 && (
            <div className="breakdown-item">
              <div className="color-indicator refunds"></div>
              <span className="label">Refunds</span>
              <span className="amount refund">{formatCurrency(totalRefundAmount)}</span>
            </div>
          )}
        </div>
      </Card>
    </RevenueCard>
  );
};

const RevenueCard = styled.div`
  flex: 1;
  min-width: 300px;

  .dashboard-card {
    height: 100%;
    overflow: visible;

    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: ${colors.text.primary};
    }

    .ant-card-body {
      padding: 16px;
      overflow: visible;
    }
  }

  .chart-container {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
    padding: 10px;
    overflow: visible;
    justify-content: center;
    flex-wrap: wrap;
  }

  .chart-wrapper {
    width: 180px;
    height: 180px;
    position: relative;
    flex-shrink: 0;
    overflow: visible;
    z-index: 10;

    .center-value {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      z-index: 5;
      pointer-events: none;

      .order-count {
        font-size: 32px;
        font-weight: 600;
        color: ${colors.text.primary};
        line-height: 1;
      }

      .label {
        font-size: 12px;
        color: ${colors.text.secondary};
        text-transform: uppercase;
        margin-top: 4px;
        line-height: 1.2;
      }
    }
  }

  .total-amount {
    flex: 1;

    .amount {
      font-size: 28px;
      font-weight: 600;
      color: ${colors.text.primary};
      margin-bottom: 8px;
    }

    .comparison {
      font-size: 14px;
      color: ${colors.text.accent};
    }
  }

  .breakdown {
    border-top: 1px solid ${colors.border.primary};
    padding-top: 16px;
  }

  .breakdown-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .color-indicator {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      margin-right: 12px;
      flex-shrink: 0;

      &.stalls {
        background-color: #2875c3;
      }
      &.rvs {
        background-color: #10ac84;
      }
      &.addons {
        background-color: #ff9f43;
      }
      &.misc {
        background-color: #8395a7;
      }
      &.cancellation {
        background-color: #576574;
      }
      &.refunds {
        background-color: #ee5253;
      }
    }

    .label {
      flex: 1;
      font-size: 14px;
      color: ${colors.text.primary};
    }

    .amount {
      font-size: 14px;
      font-weight: 500;
      color: ${colors.text.primary};

      &.refund {
        color: ${colors.error.primary};
      }
    }
  }

  @media (max-width: 768px) {
    min-width: 100%;

    .chart-container {
      flex-direction: column;
      text-align: center;
    }

    .chart-wrapper {
      width: 150px;
      height: 150px;
    }
  }
`;

export default TotalRevenueCard;
